import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useModal } from 'mui-modal-provider';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import {
  Post, Role,
} from 'lib/types';
import { RolePills } from 'components/RolePills';
import {
  FormControl, RadioGroup, FormControlLabel, Radio, TextField,
} from '@mui/material';
import { RolesProvider } from 'lib/contexts/rolesContext';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { formatLocalString } from 'lib/utils/datetime';
import { ProfileUser } from 'lib/types/users';
import { ApplicationType, JobApplication } from 'lib/types/jobs';
import { ResumeUpload } from 'features/JobDetail/ResumeUpload';
import { JobSelectPosts } from 'components/JobSelectPosts';
import { DeleteApplication } from 'features/JobDetail/DeleteApplication';
import { workTypes } from 'lib/constants/jobs';
import { RequireStar } from 'elements/RequireStar';

interface Props {
  profileUser: ProfileUser;
  open: boolean;
  onCancel: () => void;
  jobId: number;
}

export const ProspectForm = ({
  profileUser, open, onCancel, jobId,
}: Props) => {
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();

  const currentUser = useCurrentUser();

  const [application, setApplication] = useState<JobApplication|undefined>();
  const [isLoaded, setIsLoaded] = useState(false);
  const [workType, setWorkType] = useState<ApplicationType>('any');
  const [roles, setRoles] = useState<Role[]>([]);
  const [linkedinUrl, setLinkedinUrl] = useState<string|undefined>(currentUser?.linkedinUrl);

  const [posts, setPosts] = useState<Post[]>([]);

  const [resumeUrl, setResumeUrl] = useState<string | undefined>();

  const clearForm = () => {
    setWorkType('any');
    setRoles([]);
    setPosts([]);
  };

  const save = async () => {
    if (!jobId) {
      return;
    }

    const payload = {
      postIds: posts.map((p) => p.id),
      roleIds: roles.map((r) => r.id || 0),
      workType,
      ...(resumeUrl && { resumeUrl }),
      ...(linkedinUrl && { linkedinUrl }),
    };
    try {
      await API.addJobApplication(jobId, payload);
      clearForm();
      onCancel();
      enqueueSnackbar('Your application has been submitted.', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(`Error saving application: ${error}`, {
        variant: 'error',
      });
    }
  };

  const confirm = () => {
    const modal = showModal(ConfirmationModal, {
      text: 'Are you sure you would like to submit the application?',
      onNo: () => {
        modal.destroy();
      },
      onYes: () => {
        save();
        modal.destroy();
      },
    });
  };

  useEffect(() => {
    const getJobApplication = async () => {
      try {
        const result = await API.getJobApplication(jobId);
        setApplication(result);
        setIsLoaded(true);
      } catch (error) {
        enqueueSnackbar(`Error loading data: ${error}`, {
          variant: 'error',
        });
      }
    };

    if (open && jobId && currentUser) {
      getJobApplication();
    }
  }, [open, jobId, currentUser]);

  if (!currentUser) return <div>Please login to apply</div>;

  if (!isLoaded) return <div>Loading...</div>;

  if (application) {
    return (
      <>
        <h1 className="text-2xl font-semibold mb-4">Your application was submitted</h1>
        <div className="mb-4">
          You submitted an application on
          {' '}
          {formatLocalString(application.createdAt)}
          .
        </div>
        <DeleteApplication jobId={jobId} onCancel={onCancel} />
        <div className="w-full text-right">
          <button
            type="button"
            className="btn-primary"
            onClick={onCancel}
          >
            Close
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <h1 className="text-xl font-bold">
        Join the
        {' '}
        {profileUser.name}
        {' '}
        prospects list
      </h1>
      <p className="text-sm mb-4">Fill out the quick application below to apply</p>
      <FormControl size="small" className="mb-6">
        <label htmlFor="work-type">
          <RequireStar />
          Apply as full-time or freelancer?
        </label>
        <RadioGroup
          row
          aria-labelledby="radio-group"
          name="work-type"
          id="work-type"
          value={workType}
          onChange={(e) => setWorkType(e.target.value as ApplicationType)}
        >
          <FormControlLabel
            value="any"
            control={<Radio className="py-0" />}
            label={<b>{workTypes.any}</b>}
          />
          <FormControlLabel
            value="ft"
            control={<Radio className="py-0" />}
            label={<b>{workTypes.ft}</b>}
          />
          <FormControlLabel
            value="pt"
            control={<Radio className="py-0" />}
            label={<b>{workTypes.pt}</b>}
          />
        </RadioGroup>
      </FormControl>
      <div className="mb-6">
        <div className="mb-3">
          <RequireStar />
          What roles are you interested in doing? (Select up to 3)
        </div>
        <div>
          <RolesProvider>
            <RolePills
              user={currentUser}
              selectedRoles={roles}
              onChange={setRoles}
              multiple
              limit={3}
            />
          </RolesProvider>
        </div>
      </div>
      <div className="mb-6">
        <JobSelectPosts
          posts={posts}
          onChange={setPosts}
        />
      </div>
      <div className="mb-6">
        <div className="mb-3">
          Add your LinkedIn profile
        </div>
        <TextField
          id="linkedin-url"
          fullWidth
          size="small"
          placeholder="https://www.linkedin.com/in/username/"
          onChange={(e) => setLinkedinUrl(e.target.value)}
          value={linkedinUrl}
        />
      </div>
      <div className="mb-6">
        <ResumeUpload
          onUpload={setResumeUrl}
          title="Upload your resume"
        />
      </div>
      <div className="w-full text-right">
        <button
          type="button"
          className="btn-primary-outlined mr-2"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={confirm}
          type="button"
          disabled={roles.length === 0}
        >
          Apply
        </button>
      </div>
    </>
  );
};
